import { useCallback, useMemo } from 'react'
import { Form } from 'antd'
import { CustomForm } from 'components/Forms/CustomForm'
import { useSelector } from 'react-redux'
import moment from 'moment'
import { Checkbox } from '../Checkbox'
import { Select } from '../Select'
import { Datepicker } from '../Datepicker'
import { getUser } from '../../redux/store/user/getters'
import { ROLES } from '../../features/Permission'
import { LABELS, PLACEHOLDERS } from '../../constants'
import { initialFormData } from '../../features/UserContractCategories/ManageAssignmentPopupWithoutItems/constants'
import { useCRUDUserContext } from '../../pages/CRUDUser/Provider/CRUDUserProvider'
import { TCCRelatedItemReassign } from '../../features/UserContractCategories/types'
import { TCCRelatedItemReassignWithCheckbox } from '../../features/UserContractCategories/ManageAssignmentPopupWithoutItems/types'
import { TManageCategoryAssignmentProps } from './forms.manageCategoryAssignment.d'

export const ManageCategoryAssignment = ({
  form,
  onSubmit
}: TManageCategoryAssignmentProps) => {
  const user = useSelector(getUser)
  const watchIsChecked = Form.useWatch('isAssignAnotherChecked', form)

  const {
    state: { ccOwnerOptions, reassignmentRequest }
  } = useCRUDUserContext()

  const onSubmitForm = useCallback(
    (values: TCCRelatedItemReassign) => {
      onSubmit(values)
    },
    [onSubmit]
  )

  const { canSeeAssignCheckbox, canSeeEffectiveDate, canSeeCSSelect } = useMemo(
    () => ({
      canSeeAssignCheckbox:
        user.role === ROLES.VICE_PRESIDENT ||
        user.role === ROLES.COMMONS_VICE_PRESIDENT ||
        (user.role === ROLES.COMMUNITY_PRESIDENT && !reassignmentRequest?.uuid),
      canSeeEffectiveDate:
        user.role === ROLES.VICE_PRESIDENT ||
        user.role === ROLES.COMMONS_VICE_PRESIDENT ||
        (user.role === ROLES.COMMUNITY_PRESIDENT &&
          !!reassignmentRequest?.uuid),
      canSeeCSSelect:
        user.role === ROLES.COMMUNITY_PRESIDENT && !!reassignmentRequest?.uuid
    }),
    [user.role, reassignmentRequest?.uuid]
  )

  return (
    <CustomForm<TCCRelatedItemReassignWithCheckbox>
      form={form}
      initialValues={initialFormData}
      id="manage-category-assignment-form"
      className="column gap-16"
      onFinish={onSubmitForm}
    >
      {canSeeAssignCheckbox && (
        <Checkbox
          propsItem={{
            name: 'isAssignAnotherChecked',
            valuePropName: 'checked'
          }}
        >
          Assign this category to another user
        </Checkbox>
      )}
      {(watchIsChecked || canSeeCSSelect) && (
        <Select.Default
          options={ccOwnerOptions}
          propsItem={{
            label:
              user.role === ROLES.COMMONS_VICE_PRESIDENT
                ? LABELS.COMMONS_CONTRACT_STEWARD
                : LABELS.CONTRACT_STEWARD,
            name: 'assignee',
            rules: [
              {
                required: true,
                message: `${
                  user.role === ROLES.COMMONS_VICE_PRESIDENT
                    ? 'OM Lifeline c'
                    : 'C'
                }ontract steward is required`
              }
            ]
          }}
          propsSelect={{
            placeholder: PLACEHOLDERS.PLEASE_SELECT,
            getPopupContainer: (node) => document?.body ?? node
          }}
        />
      )}
      {canSeeEffectiveDate && (
        <Datepicker
          propsItem={{
            label: LABELS.EFFECTIVE_DATE,
            name: 'effective_date',
            rules: [
              {
                required: true,
                message: 'Effective date is required'
              }
            ]
          }}
          propsDate={{
            placeholder: PLACEHOLDERS.SELECT_DATE,
            disabled: user.role === ROLES.COMMUNITY_PRESIDENT,
            getPopupContainer: (node) => document?.body ?? node,
            disabledDate: (current) => current.isBefore(moment().startOf('day'))
          }}
        />
      )}
    </CustomForm>
  )
}
